import { foldAccents, normalizeText } from '../text/normalize'
import type { PhrasebookEntry } from '../types/phrasebook'

/**
 * Phrasebook search.
 *
 * Accent-folded on both sides, because she will be typing on an English
 * keyboard: "cafe" has to find "café" and "nao" has to find "não". Requiring
 * the accent would make the search useless at exactly the moment she needs it.
 *
 * Matching is per-token and prefix-based rather than substring, so "obr" finds
 * "obrigada" but "iga" does not — substring matching on a few hundred entries
 * returns noise that buries the answer.
 */

export interface SearchOptions {
  query: string
  category?: string | null
  essentialsOnly?: boolean
}

function searchKey(text: string): string {
  return foldAccents(normalizeText(text))
}

/** Pre-computed haystack for one entry, so filtering never re-normalises. */
export interface IndexedEntry {
  entry: PhrasebookEntry
  tokens: string[]
}

export function indexEntries(entries: readonly PhrasebookEntry[]): IndexedEntry[] {
  return entries.map((entry) => ({
    entry,
    // The example is included so "airport" finds the phrase whose sentence
    // mentions it, which is often how she will remember a word.
    tokens: [entry.pt, entry.en, entry.examplePt ?? '', entry.exampleEn ?? '']
      .filter(Boolean)
      .flatMap((text) => searchKey(text).split(' '))
      .filter(Boolean),
  }))
}

export function searchPhrasebook(
  indexed: readonly IndexedEntry[],
  options: SearchOptions,
): PhrasebookEntry[] {
  const terms = searchKey(options.query).split(' ').filter(Boolean)

  const matches = indexed.filter(({ entry, tokens }) => {
    if (options.essentialsOnly && !entry.tripCritical) return false
    if (options.category && entry.category !== options.category) return false
    // Every term must match something — narrowing as she types is the point.
    return terms.every((term) => tokens.some((token) => token.startsWith(term)))
  })

  if (terms.length === 0) return matches.map((m) => m.entry)

  // Rank whole-word and start-of-phrase hits above mid-phrase ones, so typing
  // "obrigada" puts the word itself above every sentence that contains it.
  return matches
    .map(({ entry }) => ({ entry, score: rank(entry, terms) }))
    .sort((a, b) => b.score - a.score)
    .map((m) => m.entry)
}

function rank(entry: PhrasebookEntry, terms: readonly string[]): number {
  const pt = searchKey(entry.pt)
  const en = searchKey(entry.en)
  let score = 0

  for (const term of terms) {
    if (pt === term || en === term) score += 100
    else if (pt.startsWith(term) || en.startsWith(term)) score += 50
    else if (pt.includes(term) || en.includes(term)) score += 10
  }

  // A tie between a trip-critical word and a rare one should go to the one she
  // is more likely to be hunting for.
  if (entry.tripCritical) score += 5
  return score
}
